import { DomainKeys, type Peer } from '../state/types/state.ts';
import type { JoinedPeer } from '../state/peers.ts';
import { type Draft, produce } from 'immer';

export type WithRequired<Type, Key extends keyof Type> = Type & {
    [Property in Key]-?: Type[Property]
}

export type Omit<Type, Key extends keyof Type> = Pick<Type, Exclude<keyof Type, Key>>
export type PartialBy<Type, Key extends keyof Type> = Pick<Partial<Type>, Key> & Omit<Type, Key>;

export function removev<T>(el: T, c: T[]): T[] {
    return c.reduce((r: T[], e: T) => {
        if (e === el) {
            return r;
        }
        return r.concat(e);
    }, [] as T[]);
}

export function ensureDomain<D extends DomainKeys>(peer: Peer, domain: D): JoinedPeer<D> {
    if (peer[domain]) {
        return peer as JoinedPeer<D>;
    }
    return produce(peer, (p: Draft<Peer>) => {
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        p[domain] = {} as unknown as any;
    }) as JoinedPeer<D>;
}

export function friendlily(o) {
    // Set => array
    // Map => object
    // function is not tested
    if (o instanceof Set) {
        return Array.from(o as Set<unknown>).map(friendlily);
    } else if (o instanceof Array) {
        return o.map(friendlily);
    } else if (o instanceof Map) {
        return friendlily(Object.fromEntries(o));
    } else if (o instanceof Object) {
        return Object.keys(o).reduce((a, k) => {
            a[k] = friendlily(o[k]);
            return a;
        }, {});
    } else {
        return o;
    }
}

function replacerWithPath(replacer: (this: unknown, field: string | number, value: unknown, path: string) => unknown): (this: unknown, field: string | number, value: unknown) => unknown {
    const paths = new Map<unknown, string>();
    return function (this: unknown, field, value) {
        const path = paths.get(this) + (Array.isArray(this) ? `[${field}]` : `.${field}`);
        if (value === Object(value)) paths.set(value, path);
        if (value instanceof RegExp) value = `#${value.source}`;
        return replacer.call(this, field, value, path.replace(/undefined\.\.?/, ''));
    };
}
/**
 * Creates a function to be used as JSON.stringify() replacer that will replace values for fields in specified paths.
 *
 * @param paths field paths to be sanitized.
 */
export function sanitizerOf(...paths: string[]): (this: unknown, field: string | number, value: unknown) => unknown {
    return replacerWithPath((field: string | number, value: unknown, path: string) => {
        if (paths.indexOf(path) !== -1) {
            return '******';
        }
        return value;
    });
}

const KEY_COMPARE_FN: ((a: string, b: string) => number) | undefined = (a: string, b: string) => a.localeCompare(b);
/**
 * Converts an object to a string representations that is stable enough to be used as property or map key.
 */
export function objectAsKey(obj: Record<string, unknown>) {
    return JSON.stringify(obj, Object.keys(obj).sort(KEY_COMPARE_FN));
}

export function some<T>(e: T | undefined): e is T {
    return  !!e;
}
